import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Minus, Plus, Sparkles, Trash2, Upload } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { uploadArtwork } from "@/components/signs/useSignConfig";
import { dpiLevel, dpiOf } from "@/lib/gangsheet";
import NamesNumbers from "@/components/shirts/gang/NamesNumbers";

const DOT = { good: "#16a34a", warn: "#f59e0b", bad: "#dc2626", unknown: "#a1a1aa" };
const DOT_LABEL = { good: "300+ dpi", warn: "low-ish dpi", bad: "too low dpi", unknown: "dpi ?" };

/** One uploaded design and every copy of it on the sheet. */
function ImageGroup({ g, sheet, theme }) {
    const level = dpiLevel(dpiOf(g));
    const btn = "grid h-6 w-6 place-items-center rounded-full border border-zinc-300 text-zinc-700 hover:border-zinc-500";
    return (
        <div className="rounded-xl border border-zinc-200 bg-white p-2.5" data-testid={`dtf-item-${g.fileId}`}>
            <div className="flex gap-2.5">
                <img src={g.url} alt={g.name} className="h-12 w-12 shrink-0 rounded-lg border border-zinc-200 bg-white object-contain p-1" />
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <p className="truncate text-xs font-bold text-zinc-900" title={g.name}>{g.name || "artwork"}</p>
                        <button type="button" onClick={() => sheet.removeGroup(g.fileId)} className="text-zinc-400 hover:text-red-600" data-testid={`dtf-remove-${g.fileId}`}><Trash2 size={13} /></button>
                    </div>
                    <p className="mt-0.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500" data-testid={`dpi-${g.fileId}`}>
                        <span className="h-2 w-2 rounded-full" style={{ background: DOT[level] }} /> {dpiOf(g) ? `${dpiOf(g)} dpi` : DOT_LABEL[level]}
                    </p>
                </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
                <input type="number" step="0.25" min="0.5" max={sheet.film.printable_width_inches} value={g.wIn}
                    onChange={(e) => sheet.setGroupWidth(g.fileId, Number(e.target.value) || 0)}
                    onFocus={(e) => (e.target.style.borderColor = theme?.primary)} onBlur={(e) => (e.target.style.borderColor = "")}
                    className="w-16 rounded-md border border-zinc-300 bg-white px-2 py-1 font-mono text-xs text-zinc-900 outline-none"
                    data-testid={`dtf-width-${g.fileId}`} />
                <span className="font-mono text-[10px] text-zinc-500">in wide × {g.hIn}"</span>
                <span className="ml-auto flex items-center gap-1">
                    <button type="button" className={btn} onClick={() => sheet.setGroupQty(g.fileId, g.uids.length - 1)} data-testid={`dtf-minus-${g.fileId}`}><Minus size={11} /></button>
                    <span className="w-6 text-center font-mono text-sm font-bold text-zinc-900" data-testid={`dtf-qty-${g.fileId}`}>{g.uids.length}</span>
                    <button type="button" className={btn} onClick={() => sheet.setGroupQty(g.fileId, g.uids.length + 1)} data-testid={`dtf-plus-${g.fileId}`}><Plus size={11} /></button>
                </span>
            </div>
            <button type="button" onClick={() => sheet.fillFrom(g.uids[0])} data-testid={`dtf-fill-${g.fileId}`}
                className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-lg border border-zinc-200 py-1 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50">
                <Sparkles size={11} /> Fill the sheet with this one
            </button>
        </div>
    );
}

/** The left rail of the gang-sheet builder: add images, add names & numbers, tune each design. */
export default function ImagePanel({ sheet, theme, className = "" }) {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [over, setOver] = useState(false);

    const addFiles = async (files) => {
        const list = Array.from(files || []);
        if (!list.length) return;
        setBusy(true);
        try {
            for (const file of list) {
                const url = URL.createObjectURL(file);
                const { w, h } = await new Promise((res) => {
                    const img = new Image();
                    img.onload = () => res({ w: img.naturalWidth || 900, h: img.naturalHeight || 900 });
                    img.onerror = () => res({ w: 900, h: 900 });
                    img.src = url;
                });
                const rec = await uploadArtwork(file);
                const ratio = w / h;
                const wIn = Math.min(sheet.film.printable_width_inches, Math.max(1, Math.round((w / 300) * 4) / 4));
                sheet.addItem({
                    fileId: rec.id, file: rec, url, name: file.name, ratio, px: { w, h },
                    wIn, hIn: Math.round((wIn / ratio) * 100) / 100,
                });
            }
            toast.success("Nested onto the sheet");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <aside className={`flex flex-col gap-3 overflow-y-auto bg-white p-3 ${className}`} data-testid="image-panel"
            onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); addFiles(e.dataTransfer.files); }}>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Images</p>
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="dtf-upload-btn"
                className={`flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed px-3 py-5 text-center transition-colors ${over ? "bg-zinc-50" : "border-zinc-300 hover:bg-zinc-50"}`}
                style={over ? { borderColor: theme?.primary } : undefined}>
                {busy ? <Loader2 className="animate-spin" size={18} style={{ color: theme?.primary }} /> : <Upload size={18} style={{ color: theme?.primary }} />}
                <span className="text-xs font-bold text-zinc-800">Add images (several at once is fine)</span>
                <span className="text-[11px] text-zinc-500">PNG · JPG · SVG · WEBP · PDF — 300 dpi is best</span>
            </button>
            <input ref={fileRef} type="file" multiple className="hidden" accept="image/*,.pdf"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="dtf-file-input" />
            <NamesNumbers sheet={sheet} theme={theme} />

            {sheet.groups.length > 0 && (
                <>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500" data-testid="dtf-placed-count">{sheet.items.length} placed</p>
                    {sheet.groups.map((g) => <ImageGroup key={g.fileId} g={g} sheet={sheet} theme={theme} />)}
                </>
            )}
            <p className="mt-auto text-[11px] leading-snug text-zinc-400">
                We nest everything for you with a {`0.5"`} gap. Drag, resize or rotate anything on the sheet.
            </p>
        </aside>
    );
}
